import { Component, computed, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { selectApplication, selectLoading, selectPending } from '../../../store/selectors/application.selectors';
import { Store } from '@ngrx/store';
import { loadApplication } from '../../../store/actions/application.actions';
import {
  ApplicantAppEntityApplicationRead,
  ApplicationAppEntityApplicationRead,
  ServiceAttachmentPositionAppEntityApplicationRead
} from '../../../openapi';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { AttachmentPaymentForm } from './attachment.payment.form';
import { ContentComponent } from '../../../components/content/content.component';
import { BreadcrumbsComponent } from '../../../common/breadcrumbs/breadcrumbs.component';
import { SpinnerComponent } from '../../../common/spinner/spinner.component';
import { ReactiveFormsModule } from '@angular/forms';
import { ValidationErrorsComponent } from '../../../common/validation-errors/validation-errors.component';
import { LabelComponent } from '../../../common/label/label.component';
import { CommonModule } from '@angular/common';
import { AlertComponent } from '../../../common/alert/alert.component';
import { PaymentCommonService } from '../../../services/payment.common.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-application-attachments-payment-page',
  standalone: true,
  imports: [
    ContentComponent,
    BreadcrumbsComponent,
    SpinnerComponent,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    LabelComponent,
    CommonModule,
    RouterModule,
    AlertComponent
  ],
  providers: [AttachmentPaymentForm],
  templateUrl: './application-attachments-payment-page.component.html'
})
export class ApplicationAttachmentsPaymentPageComponent implements OnInit, OnDestroy {

  private route: ActivatedRoute = inject(ActivatedRoute);

  private appId: string | undefined = this.route.snapshot.params['appId'];

  public form: AttachmentPaymentForm = inject(AttachmentPaymentForm);

  private store$: Store = inject(Store);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  public paymentService: PaymentCommonService = inject(PaymentCommonService);

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  private fetchApplication: Subscription = toObservable(this.application).subscribe(app => {
    this.form.createAttachmentPositions(app);
  });

  public payers: Signal<ApplicantAppEntityApplicationRead[]> = computed(() => this.application()?.applicants || []);

  public attachmentPositions: Signal<ServiceAttachmentPositionAppEntityApplicationRead[]> = computed(() => this.application()?.service?.serviceAttachmentPositions?.filter(v => !!v?.fee) || []);

  public get goBackPath(): string {
    return `../../../wnioski-zatwierdzone/${this.application()?.service?.url}/${this.application()?.uuid}`;
  }

  public onSubmit(): void {
    if (this.form.valid) {
      const payer: ApplicantAppEntityApplicationRead | null = this.form.value.payer;
      this.paymentService.goToPay({
        clientFirstName: payer?.firstName,
        clientLastName: payer?.lastName,
        clientName: payer?.name,
        positions: this.form.attachmentPositions.value,
        returnUrl: `${environment.ssl ? 'https' : 'http'}://${window.location.host}/wnioski-zatwierdzone${this.application()?.service?.url}/${this.application()?.uuid}`
      }).toPromise().then();
    } else {
      this.form.markAllAsTouched();
    }
  }

  public ngOnInit(): void {
    if (this.appId) {
      this.store$.dispatch(loadApplication({ id: this.appId }));
    }
  }

  public ngOnDestroy(): void {
    this.fetchApplication.unsubscribe();
  }
}
